import { PermissionCrudScope } from '../components/PermissionCrudScope';
import { toCamelCase } from '../featureFormConstants';
import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';
import type { FeatureFormApi } from '../useFeatureForm';

export function PermissionsStep({ form }: { form: FeatureFormApi }) {
  const {
    moduleInput, setModuleInput, moduleManual, setModuleManual, lockModule, key,
    showModuleSuggest, setShowModuleSuggest, moduleSuggestions,
    description, setDescription, primaryAction, setPrimaryAction, scopeActions,
    handleScopeToggle, actionCatalog, isPermsLoading, errors,
  } = form;

  return (
    <div className="space-y-5">
      <p
        className="text-sm"
        style={{ color: SOVEREIGN.kpiCaption }}
      >
        Tie this feature to a permission module and choose which actions roles can grant.
      </p>

      <div>
        <label
          className="block text-xs font-semibold mb-1"
          style={{ color: SOVEREIGN.ink }}
        >
          Module Name <span className="text-red-500">*</span>
          <span
            className="ml-2 font-normal"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            — groups permissions in roles{lockModule ? ' (locked)' : ''}
          </span>
        </label>
        <div className="relative">
          <input
            type="text"
            value={moduleInput}
            disabled={lockModule}
            onChange={e => { setModuleInput(e.target.value); setModuleManual(true); setShowModuleSuggest(true); }}
            onFocus={() => setShowModuleSuggest(true)}
            onBlur={() => setTimeout(() => setShowModuleSuggest(false), 150)}
            placeholder="e.g., projectMaster, userManagement"
            className="w-full px-3 py-2 text-sm font-mono rounded-lg focus:ring-2 focus:outline-none transition-colors"
            style={
              lockModule
                ? { border: `1px solid ${SOVEREIGN.innerRule}`, background: SOVEREIGN.paper, color: SOVEREIGN.kpiCaption }
                : errors.module
                  ? { border: '1px solid #fca5a5', background: '#fff', color: SOVEREIGN.ink }
                  : { border: `1px solid ${SOVEREIGN.inputBorder}`, background: SOVEREIGN.inputBg, color: SOVEREIGN.ink }
            }
          />
          {!lockModule && moduleManual && (
            <button
              type="button"
              onClick={() => { setModuleManual(false); setModuleInput(toCamelCase(key)); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs transition-colors"
              style={{ color: SOVEREIGN.kpiCaption }}
            >
              Auto
            </button>
          )}
          {!lockModule && showModuleSuggest && moduleSuggestions.length > 0 && (
            <div
              className="absolute z-10 top-full mt-1 w-full rounded-xl shadow-xl max-h-48 overflow-y-auto"
              style={{
                background: SOVEREIGN.cardBg,
                border: `1px solid ${SOVEREIGN.cardBorder}`,
              }}
            >
              {moduleSuggestions.map(m => (
                <button
                  key={m.key}
                  type="button"
                  onMouseDown={() => { setModuleInput(m.key); setModuleManual(true); setShowModuleSuggest(false); }}
                  className="w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors"
                  style={{ color: SOVEREIGN.ink }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(31,90,71,0.06)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                >
                  <span
                    className="font-mono"
                    style={{ color: SOVEREIGN.ink }}
                  >
                    {m.key}
                  </span>
                  <span style={{ color: SOVEREIGN.kpiCaption, fontSize: '0.75rem' }}>{m.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {errors.module && <p className="text-xs text-red-600 mt-1">{errors.module}</p>}
        <p
          className="text-xs mt-1"
          style={{ color: SOVEREIGN.kpiCaption }}
        >
          camelCase, e.g., projectMaster. Ties this feature to a role permission group.
        </p>
      </div>

      <div>
        <label
          className="block text-xs font-semibold mb-1"
          style={{ color: SOVEREIGN.ink }}
        >
          Description{' '}
          <span
            className="font-normal"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            (optional)
          </span>
          <span
            className="ml-2 font-normal"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            — shown as tooltip in role permission matrix
          </span>
        </label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={2}
          placeholder="Brief description of what this module does"
          className="w-full px-3 py-2 text-sm rounded-lg focus:ring-2 focus:outline-none resize-none"
          style={{
            border: `1px solid ${SOVEREIGN.inputBorder}`,
            background: SOVEREIGN.inputBg,
            color: SOVEREIGN.ink,
          }}
        />
      </div>

      <PermissionCrudScope
        moduleName={moduleInput}
        primaryAction={primaryAction}
        scopeActions={scopeActions}
        actionCatalog={actionCatalog}
        isLoading={isPermsLoading}
        onPrimaryChange={setPrimaryAction}
        onScopeToggle={handleScopeToggle}
      />
      {errors.perm && <p className="text-xs text-red-600 mt-2">{errors.perm}</p>}
    </div>
  );
}
